import React, {useContext, useEffect, useState} from 'react';
import {Nav, Navbar} from 'react-bootstrap';
import {NavLink} from 'react-router-dom';

import '../frame/SideBar.css';
import AppContext from "../../AppContext";
import apiClient from "../../web/ApiClient";

type MenuItem = {
   name: string | null;
   separator: boolean;
   items: MenuItem[] | null;
   link: string | null;
   permission: string | null;
   id: string | null;
};

/**
 * Data driven dynamic sidebar (functional component style)
 * - data fetched from menu API
 */
const DynamicSideBar = () => {
   const [menu, setMenu] = useState<MenuItem[]>([]);
   const context = useContext(AppContext);

   useEffect(() => {
      const fetchMenu = async () => {
         try {
            const response = await apiClient.get('/ams/api/v1/menu', {});
            setMenu(response.data.data);
         } catch (error) {
            console.error('Failed to fetch menu:', error);
         }
      };

      fetchMenu();
   }, []);

   /**
    * Determine whether the MenuItem should have the 'active' className applied initially based on the
    * referrer link (held in the context currentPage property).
    *
    * @param menuItem MenuItem of the current item being processed
    * @return true if active is required
    */
   const isActive = ( menuItem:MenuItem) : boolean  => {
      if (menuItem.link && menuItem.link.includes('?')){

         const paramsString = menuItem.link.split('?')[1]; // extract paramsString
         const params = new URLSearchParams(paramsString);
         let pageValue = params.get('page');

         // Workaround for issue with context.currentPage which can be of the form 'course' to compare against 'course-list' versus
         // 'quiz-start' to compare against 'quiz-list' versus. Considering first part for comparison. (Future: should have a linking param)
         const contextPageParam =  context ? context.currentPage.split('-')[0] : null;

         // compare menu item page param with calling context page param
         if (pageValue && contextPageParam && pageValue.startsWith(contextPageParam)) {
            return true
         }
      }
      return false;
   }

   /**
    * Prep URL for use
    * @param link link refined in menu definition
    * @return URL ready to be used
    */
   const buildUrl = (link: string | null): string => {
      if (!link) return '#';
      // Remove 'ui/' prefix if present (since it's part of basename)
      const cleanedLink = link.startsWith('ui/') ? link.slice(3) : link;

      // Ensure leading slash for absolute path
      return cleanedLink.startsWith('/') ? cleanedLink : '/' + cleanedLink;
   };

   /**
    * Recursively renders the menu - top down
    * @param items list of menu items to consider
    * @param level current level of tree - start level is 1 (start left padding at 20px)
    */
   const renderMenuItems = (items: MenuItem[] | null, level = 1) => {
      if (!items) return null;

      return items.map((item, idx) => {
         if (item.separator) {
            return (
               <Nav.Item key={`sep-${idx}`} className="my-2">
                  <br />
               </Nav.Item>
            );
         }

         const url = buildUrl(item.link);

         const active = isActive(item);
         const className = `nav-link${active ? ' active' : ''}`;
         // if link is externally routed, it must begin with '/'
         const isExternal = item.link && item.link.startsWith("/");

         // For when a parent component without a page to render has no children due to permissions
         const isEmptyItem = (!item.link || item.link === '') && (!item.items || item.items.length === 0);
         if (isEmptyItem) {
            return <></>;
         }

         // Render the parent display name as a <span> if no valid link or link is '#', rendering children if present.
         if (!item.link || url === '#') {
            return (
               <React.Fragment key={idx}>
                  <Nav.Item>
                       <span className={`${className} disabled`}
                             style={{ cursor: 'default', paddingLeft: 20 * level }}
                       >
                         {item.name}
                       </span>
                  </Nav.Item>
                  {item.items && renderMenuItems(item.items, level + 1)}
               </React.Fragment>
            );
         }

         // If item has children and valid link
         if (item.items && item.items.length > 0) {
            return (
               <React.Fragment key={idx}>
                  <Nav.Item>
                     {isExternal ? (
                        <a href={url} className={className} style={{ paddingLeft: 20 * level }}>{item.name}</a>
                        ) : (
                        <NavLink to={url} className={className} style={{ paddingLeft: 20 * level }}>{item.name}</NavLink>
                     )}
                  </Nav.Item>
                  {renderMenuItems(item.items, level + 1)}
               </React.Fragment>
            );
         } else {
            // Leaf nodes: use 'end' so active only on exact match
            return (
               <Nav.Item key={idx}>
                  {isExternal ? (
                     <a href={url} className={className} style={{ paddingLeft: 20 * level }}>
                        {item.name}
                     </a>
                     ) : (
                     <NavLink to={url} className={className} style={{ paddingLeft: 20 * level }} end>
                        {item.name}
                     </NavLink>
                  )}
               </Nav.Item>
            );
         }
      });
   };
   return (
      <Navbar className="sidebar flex-column" bg="light" variant="light">
         <Nav className="flex-column">{renderMenuItems(menu)}</Nav>
      </Navbar>
   );
};

export default DynamicSideBar;
